<script setup>
import { computed } from 'vue';

/**
 * LaisStatusBadge — canonical status pill for every LAIS domain.
 *
 * Never style a status inline in a page. Pass a semantic `tone` and let this
 * component own the color mapping. Unknown/unrecognized tones fall back to
 * `neutral` so an unknown backend state is never rendered as success.
 */
const props = defineProps({
  label: { type: [String, Number], default: '' },
  tone: { type: String, default: 'neutral' },
  icon: { type: String, default: '' },
  // shows a leading status dot instead of/alongside an icon
  dot: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  title: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  uppercase: { type: Boolean, default: false },
});

const TONE_ALIASES = Object.freeze({
  neutral: 'neutral',
  unknown: 'neutral',
  muted: 'neutral',
  default: 'neutral',
  info: 'info',
  active: 'info',
  running: 'info',
  processing: 'info',
  positive: 'positive',
  success: 'positive',
  ok: 'positive',
  healthy: 'positive',
  done: 'positive',
  completed: 'positive',
  warning: 'warning',
  pending: 'warning',
  stale: 'warning',
  paused: 'warning',
  danger: 'danger',
  error: 'danger',
  failed: 'danger',
  breach: 'danger',
  revoked: 'danger',
  blocked: 'danger',
});

const TONE_CLASSES = Object.freeze({
  neutral: 'bg-lais-bg-soft text-lais-text-body border-lais-border-soft',
  info: 'bg-lais-primary-50 text-lais-primary border-lais-primary-100',
  positive: 'bg-lais-mint/15 text-lais-mint-dark border-lais-mint/30',
  warning: 'bg-lais-warning/15 text-lais-text border-lais-warning/40',
  danger: 'bg-lais-danger-50 text-lais-danger border-lais-danger/40',
});

const DOT_CLASSES = Object.freeze({
  neutral: 'bg-lais-text-muted',
  info: 'bg-lais-primary',
  positive: 'bg-lais-mint-dark',
  warning: 'bg-lais-warning',
  danger: 'bg-lais-danger',
});

const resolvedTone = computed(
  () => TONE_ALIASES[String(props.tone || '').toLowerCase()] || 'neutral'
);
const toneClass = computed(() => TONE_CLASSES[resolvedTone.value]);
const dotClass = computed(() => DOT_CLASSES[resolvedTone.value]);
const sizeClass = computed(() =>
  props.size === 'sm'
    ? 'h-5 gap-1 px-2 text-lais-label-xs'
    : 'h-6 gap-1.5 px-2.5 text-lais-label-sm'
);
const iconSizeClass = computed(() =>
  props.size === 'sm' ? 'size-3' : 'size-3.5'
);
const displayLabel = computed(() =>
  props.label === null || props.label === undefined ? '' : String(props.label)
);
const accessibleLabel = computed(
  () => props.ariaLabel || displayLabel.value || undefined
);
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center rounded-full border font-semibold leading-none"
    :class="[
      toneClass,
      sizeClass,
      uppercase ? 'uppercase tracking-[0.04em]' : '',
    ]"
    :data-tone="resolvedTone"
    :title="title || undefined"
    :aria-label="accessibleLabel"
    role="status"
  >
    <span
      v-if="dot"
      class="size-1.5 shrink-0 rounded-full"
      :class="dotClass"
      aria-hidden="true"
    />
    <span
      v-else-if="icon"
      class="shrink-0"
      :class="[icon, iconSizeClass]"
      aria-hidden="true"
    />
    <span v-if="displayLabel" class="truncate">{{ displayLabel }}</span>
  </span>
</template>
